import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DefaultService, LeagueOutput } from '../generated-api';
import {
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  ArrowPathIcon,
  TrophyIcon,
  DocumentTextIcon,
  ChartBarIcon,
  SparklesIcon,
  UserIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import { useData } from '../contexts/DataContext';
import { usePlayRecapData } from '../hooks/usePlayRecapData';
import LeagueTabNavigation from '../components/LeagueTabNavigation';
import CompactRatingDisplay from '../components/common/CompactRatingDisplay';
import { TEAM_COLORS } from '../components/SortableEntity';
import {
  PlayerRatingImpactCard,
  RecentFormIndicator,
  StreakDisplay,
  LeaguePositionChange,
  RecapFunFacts,
  RecapSkeleton,
  computeLeaguePositionBefore,
  computeLeaguePositionAfter,
  computeFunFacts,
} from '../components/recap';

const PlayRecapPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { leagues: contextLeagues } = useData();
  const data = usePlayRecapData(id);
  const [selectedLeagueTab, setSelectedLeagueTab] = useState<string>('');
  const [allLeagues, setAllLeagues] = useState<LeagueOutput[]>([]);

  const { play, game, players } = data;

  // Fetch leagues including game-specific ones so they appear in tabs
  useEffect(() => {
    if (!play?.leagueIds?.length) return;

    const missingIds = play.leagueIds.filter(lid => !contextLeagues.find(l => l.id === lid));
    if (missingIds.length === 0) {
      setAllLeagues(contextLeagues);
      return;
    }

    DefaultService.getLeaguesLeaguesGet({ includeGameSpecific: true })
      .then(setAllLeagues)
      .catch(() => setAllLeagues(contextLeagues));
  }, [play?.leagueIds, contextLeagues]);

  // Set default league tab when play loads
  useEffect(() => {
    if (play?.leagueIds?.length && !selectedLeagueTab) {
      setSelectedLeagueTab(play.leagueIds[0]);
    }
  }, [play, selectedLeagueTab]);

  // All player IDs in this play
  const allPlayerIds = useMemo(() => {
    if (!play) return [];
    return Object.values(play.teams).flat();
  }, [play]);

  // Map playerId → Player object
  const playerMap = useMemo(() => {
    const map: Record<string, typeof players[number]> = {};
    for (const p of players) {
      map[p.id] = p;
    }
    return map;
  }, [players]);

  // Ordered ranked entries by ranking position — groups multi-player teams together
  const rankedEntries = useMemo(() => {
    if (!play) return [];

    const hasMultiPlayerTeams = Object.values(play.teams).some(ids => ids.length > 1);

    if (!hasMultiPlayerTeams) {
      // Solo mode: flat list of players
      const result: { type: 'player'; playerId: string; position: number }[] = [];
      let pos = 1;
      for (const rankItem of play.ranking) {
        const teamNames = typeof rankItem === 'string' ? [rankItem] : rankItem;
        const playerIdsInTie: string[] = [];
        for (const teamName of teamNames) {
          const ids = play.teams[teamName] || [];
          playerIdsInTie.push(...ids);
        }
        for (const pid of playerIdsInTie) {
          result.push({ type: 'player', playerId: pid, position: pos });
        }
        pos += playerIdsInTie.length;
      }
      return result;
    }

    // Team mode: group players under their team
    const result: ({ type: 'player'; playerId: string; position: number } | { type: 'team'; teamName: string; playerIds: string[]; position: number })[] = [];
    let pos = 1;
    for (const rankItem of play.ranking) {
      const teamNames = typeof rankItem === 'string' ? [rankItem] : rankItem;
      for (const teamName of teamNames) {
        const ids = play.teams[teamName] || [];
        if (ids.length > 1) {
          result.push({ type: 'team', teamName, playerIds: ids, position: pos });
        } else {
          for (const pid of ids) {
            result.push({ type: 'player', playerId: pid, position: pos });
          }
        }
      }
      const totalPlayers = teamNames.reduce((sum, tn) => sum + (play.teams[tn]?.length || 0), 0);
      pos += totalPlayers;
    }
    return result;
  }, [play]);

  // Fun facts per player
  const funFacts = useMemo(() => {
    if (!play || !game || data.extendedLoading) return [];
    const facts: { icon: string; message: string }[] = [];
    for (const pid of allPlayerIds) {
      const playerName = playerMap[pid]?.name || 'Unknown';
      const recentPlays = data.recentPlaysPerPlayer[pid] || [];
      facts.push(...computeFunFacts(recentPlays, pid, playerName, game.name, play));
    }
    return facts;
  }, [play, game, allPlayerIds, playerMap, data.recentPlaysPerPlayer, data.extendedLoading]);

  // League position changes
  const positionChanges = useMemo(() => {
    if (!selectedLeagueTab || data.extendedLoading) return [];
    const standings = data.leagueStandings[selectedLeagueTab];
    const ratingsBefore = data.leagueRatingsBefore[selectedLeagueTab];
    if (!standings || !ratingsBefore) return [];

    return allPlayerIds
      .filter(pid => ratingsBefore[pid])
      .map(pid => {
        const posBefore = computeLeaguePositionBefore(standings, pid, ratingsBefore[pid]);
        const posAfter = computeLeaguePositionAfter(standings, pid);
        return {
          playerId: pid,
          playerName: playerMap[pid]?.name || 'Unknown',
          positionBefore: posBefore,
          positionAfter: posAfter,
        };
      });
  }, [selectedLeagueTab, data.leagueStandings, data.leagueRatingsBefore, allPlayerIds, playerMap, data.extendedLoading]);

  // ── Loading / Error states ────────────────────────────────────────

  if (data.coreLoading) {
    return <RecapSkeleton />;
  }

  if (data.error || !play) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-heading mb-4">
          {data.error || 'Play Session Not Found'}
        </h2>
        <Link
          to="/plays"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Play Sessions
        </Link>
      </div>
    );
  }

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
  };

  const gameName = game?.name || 'Unknown Game';

  return (
    <div className="container mx-auto px-0 py-4 sm:px-4 sm:py-8 space-y-4 sm:space-y-6">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="bg-surface sm:shadow-sm sm:rounded-xl sm:border sm:border-edge overflow-hidden">
        <div className="p-4 sm:p-6">
          <div className="flex items-start gap-4">
            {/* Back arrow */}
            <Link
              to={`/plays/${play.id}`}
              className="mt-1 text-muted hover:text-heading transition-colors"
              aria-label="Back to play details"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>

            {/* Game image */}
            {game?.imageUrl ? (
              <img
                src={game.imageUrl}
                alt={gameName}
                className="w-20 h-20 sm:w-24 sm:h-24 object-contain rounded-xl bg-surface-base flex-shrink-0"
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-surface-subtle flex items-center justify-center flex-shrink-0">
                <span className="text-3xl text-faint">
                  <TrophyIcon className="h-8 w-8 text-stone-300" />
                </span>
              </div>
            )}

            {/* Title + pills */}
            <div className="flex-1 min-w-0">
              <h1 className="text-fluid-h1 font-bold text-heading leading-tight font-serif">
                {gameName}
              </h1>
              <div className="text-sm text-muted mt-0.5">Game Recap</div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {new Date(play.playDate).toLocaleDateString()}
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                  <UserGroupIcon className="h-3.5 w-3.5" />
                  {allPlayerIds.length} players
                </span>
                {play.durationMinutes != null && play.durationMinutes > 0 && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                    <ClockIcon className="h-3.5 w-3.5" />
                    {formatDuration(play.durationMinutes)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Achievements ──────────────────────────────────────────── */}
      {!data.extendedLoading && funFacts.length > 0 && (
        <div className="bg-surface px-4 py-5 sm:shadow sm:rounded-xl sm:border sm:border-edge sm:p-6">
          <h2 className="text-lg font-serif font-semibold text-heading mb-3 flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-amber-500" />
            Achievements
          </h2>
          <RecapFunFacts facts={funFacts} />
        </div>
      )}

      {/* ── Rating Impact (per league) ─────────────────────────────── */}
      <div className="bg-surface px-4 py-5 sm:shadow sm:rounded-xl sm:border sm:border-edge sm:p-6">
        <h2 className="text-lg font-serif font-semibold text-heading mb-3 flex items-center gap-2">
          <ChartBarIcon className="h-5 w-5 text-primary-500" />
          Rating Impact
        </h2>

        <LeagueTabNavigation
          leagues={allLeagues}
          playLeagueIds={play.leagueIds}
          selectedLeagueId={selectedLeagueTab}
          onLeagueSelect={setSelectedLeagueTab}
        />

        {/* Rating cards — grouped by team when teams have multiple players */}
        <div className="space-y-3">
          {rankedEntries.map((entry) => {
            if (entry.type === 'player') {
              const player = playerMap[entry.playerId];
              if (!player) return null;

              const ratingsBefore = selectedLeagueTab
                ? data.leagueRatingsBefore[selectedLeagueTab]?.[entry.playerId]
                : undefined;
              const ratingsAfter = selectedLeagueTab
                ? data.leagueRatingsAfter[selectedLeagueTab]?.[entry.playerId]
                : undefined;
              const ratingChange = selectedLeagueTab
                ? data.leagueRatingChanges[selectedLeagueTab]?.[entry.playerId]
                : undefined;
              const winProbability = selectedLeagueTab
                ? data.leagueWinProbabilities[selectedLeagueTab]?.[entry.playerId]
                : undefined;

              return (
                <PlayerRatingImpactCard
                  key={entry.playerId}
                  player={player}
                  position={entry.position}
                  totalPlayers={allPlayerIds.length}
                  ratingBefore={ratingsBefore}
                  ratingAfter={ratingsAfter}
                  ratingChange={ratingChange}
                  winProbability={winProbability}
                  ratingsReady={data.ratingsReady}
                  ratingsTimedOut={data.ratingsTimedOut}
                />
              );
            }

            // Team entry — render grouped card
            const teamColorName = entry.teamName.replace(/^team\s*/i, '').toLowerCase();
            const teamColor = TEAM_COLORS.find(c => c.name.toLowerCase() === teamColorName) || null;

            // Aggregate team rating change
            const memberChanges = entry.playerIds
              .map(pid => selectedLeagueTab ? data.leagueRatingChanges[selectedLeagueTab]?.[pid] : undefined)
              .filter((c): c is number => c !== undefined);
            const teamRatingChange = memberChanges.length > 0
              ? memberChanges.reduce((sum, c) => sum + c, 0)
              : undefined;

            // Team combined rating after
            const memberRatingsAfter = entry.playerIds
              .map(pid => selectedLeagueTab ? data.leagueRatingsAfter[selectedLeagueTab]?.[pid] : undefined)
              .filter((r): r is { rating: number; sigma: number } => r !== undefined);
            const teamRatingAfter = memberRatingsAfter.length > 0
              ? memberRatingsAfter.reduce((sum, r) => sum + r.rating, 0)
              : null;
            const teamSigmaAfter = memberRatingsAfter.length > 0
              ? Math.sqrt(memberRatingsAfter.reduce((sum, r) => sum + r.sigma * r.sigma, 0))
              : null;

            return (
              <div
                key={entry.teamName}
                className={`rounded-xl border overflow-hidden
                           ${teamColor ? teamColor.border : 'border-edge'}`}
              >
                {/* Team header */}
                <div className={`flex items-center justify-between px-3 py-2
                                ${teamColor ? teamColor.bg : 'bg-surface-subtle'}`}>
                  <span className={`text-xs font-bold uppercase tracking-wider
                                   ${teamColor ? teamColor.text : 'text-muted'}`}>
                    {entry.teamName}
                  </span>
                  <div className="flex items-center gap-2">
                    {data.ratingsReady && teamRatingAfter !== null && teamSigmaAfter !== null && (
                      <CompactRatingDisplay rating={teamRatingAfter} sigma={teamSigmaAfter} className="text-[10px]" />
                    )}
                    {data.ratingsReady && teamRatingChange !== undefined && (
                      <span className={`text-xs font-bold font-mono ${
                        teamRatingChange > 0 ? 'text-success-700' : teamRatingChange < 0 ? 'text-error-600' : 'text-body'
                      }`}>
                        {teamRatingChange > 0 ? '+' : ''}{teamRatingChange.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
                {/* Team members */}
                <div className="divide-y divide-edge">
                  {entry.playerIds.map(playerId => {
                    const player = playerMap[playerId];
                    if (!player) return null;

                    const ratingsBefore = selectedLeagueTab
                      ? data.leagueRatingsBefore[selectedLeagueTab]?.[playerId]
                      : undefined;
                    const ratingsAfter = selectedLeagueTab
                      ? data.leagueRatingsAfter[selectedLeagueTab]?.[playerId]
                      : undefined;
                    const ratingChange = selectedLeagueTab
                      ? data.leagueRatingChanges[selectedLeagueTab]?.[playerId]
                      : undefined;
                    const winProbability = selectedLeagueTab
                      ? data.leagueWinProbabilities[selectedLeagueTab]?.[playerId]
                      : undefined;

                    return (
                      <PlayerRatingImpactCard
                        key={playerId}
                        player={player}
                        position={entry.position}
                        totalPlayers={allPlayerIds.length}
                        ratingBefore={ratingsBefore}
                        ratingAfter={ratingsAfter}
                        ratingChange={ratingChange}
                        winProbability={winProbability}
                        ratingsReady={data.ratingsReady}
                        ratingsTimedOut={data.ratingsTimedOut}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Player Highlights ──────────────────────────────────────── */}
      {!data.extendedLoading && (
        <div className="bg-surface px-4 py-5 sm:shadow sm:rounded-xl sm:border sm:border-edge sm:p-6">
          <h2 className="text-lg font-serif font-semibold text-heading mb-3 flex items-center gap-2">
            <UserIcon className="h-5 w-5 text-accent-500" />
            Player Highlights
          </h2>

          {/* Mobile: horizontal scroll, Desktop: grid */}
          <div
            className="md:hidden overflow-x-auto -mx-4 px-4"
            style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            <div className="flex gap-3 snap-x snap-mandatory scroll-smooth pb-2">
              {allPlayerIds.map(pid => {
                const player = playerMap[pid];
                if (!player) return null;
                const recentPlays = data.recentPlaysPerPlayer[pid] || [];
                return (
                  <PlayerHighlightCard
                    key={pid}
                    playerName={player.name}
                    playerId={pid}
                    recentPlays={recentPlays}
                    totalPlays={recentPlays.length}
                    gameName={gameName}
                    className="flex-shrink-0 w-[65vw] max-w-[260px] snap-start"
                  />
                );
              })}
            </div>
          </div>
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {allPlayerIds.map(pid => {
              const player = playerMap[pid];
              if (!player) return null;
              const recentPlays = data.recentPlaysPerPlayer[pid] || [];
              return (
                <PlayerHighlightCard
                  key={pid}
                  playerName={player.name}
                  playerId={pid}
                  recentPlays={recentPlays}
                  totalPlays={recentPlays.length}
                  gameName={gameName}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* ── League Position Changes ────────────────────────────────── */}
      {!data.extendedLoading && positionChanges.length > 0 && (
        <div className="bg-surface px-4 py-5 sm:shadow sm:rounded-xl sm:border sm:border-edge sm:p-6">
          <h2 className="text-lg font-serif font-semibold text-heading mb-3 flex items-center gap-2">
            <ListBulletIcon className="h-5 w-5 text-stone-500" />
            League Standings Impact
          </h2>
          <div className="divide-y divide-edge">
            {positionChanges.map(pc => (
              <LeaguePositionChange
                key={pc.playerId}
                playerName={pc.playerName}
                positionBefore={pc.positionBefore}
                positionAfter={pc.positionAfter}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Footer CTAs ──────────────────────────────────────────── */}
      <div className="bg-surface px-4 py-5 sm:shadow sm:rounded-xl sm:border sm:border-edge sm:px-6 flex flex-col gap-3">
        <Link
          data-track="recap_rematch"
          to={`/plays/new?rematch=${play.id}`}
          className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 transition-colors shadow-sm"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Play Again
        </Link>
        <Link
          data-track="recap_play_details"
          to={`/plays/${play.id}`}
          className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium text-body hover:bg-surface-hover transition-colors"
        >
          <DocumentTextIcon className="h-4 w-4 mr-2" />
          Full Play Details
        </Link>
        {selectedLeagueTab && (
          <Link
            data-track="recap_league"
            to={`/leagues/${selectedLeagueTab}`}
            className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium text-body hover:bg-surface-hover transition-colors"
          >
            <TrophyIcon className="h-4 w-4 mr-2" />
            View League Standings
          </Link>
        )}
      </div>
    </div>
  );
};

// ── PlayerHighlightCard (internal) ────────────────────────────────────

interface PlayerHighlightCardProps {
  playerName: string;
  playerId: string;
  recentPlays: import('../generated-api').PlayOutput[];
  totalPlays: number;
  gameName: string;
  className?: string;
}

const PlayerHighlightCard: React.FC<PlayerHighlightCardProps> = ({
  playerName,
  playerId,
  recentPlays,
  totalPlays,
  gameName,
  className = '',
}) => (
  <div className={`rounded-lg border border-edge bg-surface-subtle p-3 ${className}`}>
    <div className="font-semibold text-heading text-sm truncate mb-2">{playerName}</div>
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted">Recent form</span>
        <RecentFormIndicator recentPlays={recentPlays} playerId={playerId} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted">Streak</span>
        <StreakDisplay recentPlays={recentPlays} playerId={playerId} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted">Plays at {gameName}</span>
        <span className="text-xs font-semibold text-heading">{totalPlays}</span>
      </div>
    </div>
  </div>
);

export default PlayRecapPage;
